// ─── JMAP → UI type adapter ───────────────────────────────────────────────────
// Maps JMAP protocol objects (JMAPEmail, JMAPMailbox, JMAPThread) into the
// existing app UI types (Thread, Message, Label) so UI components need no changes.

import type { JMAPEmail, JMAPMailbox, JMAPThread } from './jmapTypes'
import type { Thread, Message, Label, Address } from './types'

// ─── Mailbox → Label ──────────────────────────────────────────────────────────

// Stalwart JMAP role → canonical sidebar slug.
// "junk" is the RFC 8621 name; we expose it as "spam" to match sidebar slugs.
const ROLE_SLUG: Record<string, string> = {
  junk: 'spam',
}

export function mailboxToLabel(mb: JMAPMailbox): Label {
  const role = mb.role ?? null
  const slug = role ? (ROLE_SLUG[role] ?? role) : mb.name.toLowerCase().replace(/\s+/g, '_')
  return {
    id: mb.id,
    name: mb.name,
    slug,
    color: roleToColor(role),
    is_system: role !== null,
    type: role !== null ? 'folder' : 'label',
    unread_count: mb.unreadEmails,
    total_count: mb.totalEmails,
  }
}

// Virtual labels — no JMAP mailbox backing, filtered locally in the thread list.
// These are always injected so the sidebar always shows them.
const VIRTUAL_LABELS: Label[] = [
  { id: 'virtual:starred',   name: 'Starred',   slug: 'starred',   color: '#f9ab00', is_system: true, type: 'folder', unread_count: 0, total_count: 0 },
  { id: 'virtual:snoozed',   name: 'Snoozed',   slug: 'snoozed',   color: '#f9ab00', is_system: true, type: 'folder', unread_count: 0, total_count: 0 },
  { id: 'virtual:important', name: 'Important', slug: 'important', color: '#f9ab00', is_system: true, type: 'folder', unread_count: 0, total_count: 0 },
  { id: 'virtual:all_mail',  name: 'All Mail',  slug: 'all_mail',  color: '#757575', is_system: true, type: 'folder', unread_count: 0, total_count: 0 },
  { id: 'virtual:scheduled', name: 'Scheduled', slug: 'scheduled', color: '#757575', is_system: true, type: 'folder', unread_count: 0, total_count: 0 },
]

/** Convert a JMAP mailbox list to UI labels, injecting virtual labels. */
export function mailboxesToLabels(mailboxes: JMAPMailbox[]): Label[] {
  const real = mailboxes.map(mailboxToLabel)
  // Merge: real labels take priority; virtual ones fill in any gaps.
  const realSlugs = new Set(real.map(l => l.slug))
  const virtual = VIRTUAL_LABELS.filter(v => !realSlugs.has(v.slug))
  return [...real, ...virtual]
}

function roleToColor(role: string | null): string {
  switch (role) {
    case 'inbox':   return '#4285f4'
    case 'sent':    return '#34a853'
    case 'drafts':  return '#fbbc04'
    case 'trash':   return '#ea4335'
    case 'junk':    return '#ff6d00'
    case 'archive': return '#9e9e9e'
    default:        return '#757575'
  }
}

// ─── JMAPEmail → Message ──────────────────────────────────────────────────────

export function jmapEmailToMessage(email: JMAPEmail, mailboxes: JMAPMailbox[]): Message {
  const from = email.from?.[0]
  const mailboxIds = Object.keys(email.mailboxIds ?? {})
  const isSent = mailboxIds.some(id => {
    const mb = mailboxes.find(m => m.id === id)
    return mb?.role === 'sent'
  })
  const isSpam = mailboxIds.some(id => {
    const mb = mailboxes.find(m => m.id === id)
    return mb?.role === 'junk'
  })
  const isTrashed = mailboxIds.some(id => {
    const mb = mailboxes.find(m => m.id === id)
    return mb?.role === 'trash'
  })
  const isDraft = '$draft' in (email.keywords ?? {})

  // Extract body from bodyValues
  let bodyHtml: string | undefined
  let bodyText: string | undefined
  if (email.bodyValues) {
    const htmlPart = email.htmlBody?.[0]
    const textPart = email.textBody?.[0]
    if (htmlPart?.partId && email.bodyValues[htmlPart.partId]) {
      bodyHtml = email.bodyValues[htmlPart.partId].value
    }
    if (textPart?.partId && email.bodyValues[textPart.partId]) {
      bodyText = email.bodyValues[textPart.partId].value
    }
  }

  return {
    id: email.id,
    thread_id: email.threadId,
    from_address: from?.email ?? '',
    from_name: from?.name ?? '',
    to_addresses: addressList(email.to),
    cc_addresses: addressList(email.cc),
    bcc_addresses: addressList(email.bcc),
    reply_to: email.replyTo?.[0]?.email,
    subject: email.subject ?? '(no subject)',
    date: email.receivedAt,
    body_html: bodyHtml,
    body_text: bodyText,
    snippet: email.preview ?? '',
    is_read: '$seen' in (email.keywords ?? {}),
    is_starred: '$flagged' in (email.keywords ?? {}),
    is_important: '$important' in (email.keywords ?? {}),
    is_draft: isDraft,
    is_sent: isSent,
    is_spam: isSpam,
    is_trashed: isTrashed,
    spam_score: undefined,
    size_bytes: email.size,
    blob_id: email.blobId,
    attachments: email.attachments
      ?.filter(a => a.disposition !== 'inline' && a.name)
      .map(a => ({
        id: a.blobId ?? '',
        filename: a.name ?? 'attachment',
        content_type: a.type,
        size_bytes: a.size,
        is_inline: false,
      })),
  }
}

function addressList(addrs: Array<{ name: string | null; email: string }> | null | undefined): Address[] {
  return (addrs ?? []).map(a => ({ name: a.name ?? '', address: a.email }))
}

// ─── JMAPEmail list → Thread list ─────────────────────────────────────────────
// Groups a flat list of JMAPEmails (from Email/query result) into Thread objects
// for the MessageList. Each email's threadId becomes the thread's ID.
// When multiple emails share a threadId, we show the most recent one's metadata.

export function emailsToThreadList(emails: JMAPEmail[], mailboxes: JMAPMailbox[]): Thread[] {
  const threadMap = new Map<string, JMAPEmail[]>()
  for (const email of emails) {
    const arr = threadMap.get(email.threadId) ?? []
    arr.push(email)
    threadMap.set(email.threadId, arr)
  }

  const threads: Thread[] = []
  for (const [threadId, threadEmails] of threadMap) {
    // Sort newest first
    const sorted = [...threadEmails].sort(
      (a, b) => new Date(b.receivedAt).getTime() - new Date(a.receivedAt).getTime()
    )
    const latest = sorted[0]
    const allParticipants = new Set<string>()
    for (const e of sorted) {
      if (e.from?.[0]?.email) allParticipants.add(e.from[0].email)
    }

    const hasUnread = sorted.some(e => !('$seen' in (e.keywords ?? {})))
    const isStarred = sorted.some(e => '$flagged' in (e.keywords ?? {}))
    const isImportant = sorted.some(e => '$important' in (e.keywords ?? {}))

    // Derive labels from mailboxIds of the latest email
    const labels = Object.keys(latest.mailboxIds ?? {})
      .map(id => mailboxes.find(m => m.id === id))
      .filter(Boolean)
      .map(m => mailboxToLabel(m!))

    threads.push({
      id: threadId,
      subject: latest.subject ?? '(no subject)',
      snippet: latest.preview ?? '',
      message_count: sorted.length,
      unread_count: sorted.filter(e => !('$seen' in (e.keywords ?? {}))).length,
      has_attachments: sorted.some(e => e.hasAttachment),
      is_starred: isStarred,
      is_important: isImportant,
      participants: Array.from(allParticipants),
      last_message_at: latest.receivedAt,
      labels,
      messages: undefined,  // lazy — fetched on open via getThread()
    })
  }
  return threads
}

// ─── Full thread with messages ────────────────────────────────────────────────

export function buildFullThread(
  existing: Thread,
  jmapThread: JMAPThread,
  emails: JMAPEmail[],
  mailboxes: JMAPMailbox[],
): Thread {
  const messages = jmapThread.emailIds
    .map(id => emails.find(e => e.id === id))
    .filter(Boolean)
    .map(e => jmapEmailToMessage(e!, mailboxes))
  return { ...existing, messages }
}
